import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { NavTabs as NavTabs$1 } from './component.legacy.js';
import { NavTabs as NavTabs$2 } from './component.js';
import { PathHelper } from '../../utils/UrlUtil.js';
import { hasAccess } from '../../utils/AccessControlUtil.js';
import { pickDataAttributes } from '../../utils/pickDataAttributes.js';
/**
 * This component is used to create Tabs based on the routing in the index.js of a bundle.
 * It requires a few props to be set on each Route: title, routeKey, path, and the Component being rendered.
 * These Routes can be set directly or mapped through as an array of tab props to pass down for example:
 * const tabList = [
 *  {
 *    path: "compliance",
 *    routeKey: 'corporate-compliance',
 *    title: 'Conduct & Culture',
 *    component: TeamsContainer
 *  }]
 * and in the index.js:
 * {tabList.map((tab, index) => (<Route keys={index} title={tab.title} {...tab} key={tab.routeKey} />))}
 *
 * This component requires the childRoutes to be passed to it in the rendering component, example:
 * <NavTabs classNames="row organization uppercase" {...this.props} childRoutes={route.childRoutes} onHandleSelect={this.onTabSelect} />
 */

var NavTabs = function NavTabs(props) {
  var childRoutes = props.childRoutes,
      route = props.route,
      router = props.router,
      _props$alwaysShowTabs = props.alwaysShowTabs,
      alwaysShowTabs = _props$alwaysShowTabs === void 0 ? true : _props$alwaysShowTabs,
      classNames = props.classNames,
      _props$renderAsLegacy = props.renderAsLegacy,
      renderAsLegacy = _props$renderAsLegacy === void 0 ? true : _props$renderAsLegacy,
      onHandleSelect = props.onHandleSelect,
      confirmTabChange = props.confirmTabChange;
  /***
   * State
   */

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      activeTab = _useState2[0],
      setActiveTab = _useState2[1];

  var processedChildRoutes = useMemo(function () {
    return childRoutes || (route === null || route === void 0 ? void 0 : route.childRoutes) || [];
  }, [childRoutes, route === null || route === void 0 ? void 0 : route.childRoutes]);
  /***
   * Methods
   */

  var findChildRoute = useCallback(function (routeKey, childRoutes) {
    return childRoutes === null || childRoutes === void 0 ? void 0 : childRoutes.find(function (child) {
      return child.routeKey === routeKey;
    });
  }, []);
  var updateRoute = useCallback(function (routeKey) {
    var path = '';

    if (processedChildRoutes.length) {
      var childRoute = findChildRoute(routeKey, processedChildRoutes);
      path = PathHelper.ensureStartSlash(childRoute === null || childRoute === void 0 ? void 0 : childRoute.path);
    }

    if (router) {
      router.replace("".concat(router.params.id || '').concat(path));
    }
  }, [findChildRoute, processedChildRoutes, router]);
  /**
   * Check current active tab based on route
   */

  var getActiveTab = useCallback(function () {
    var currentPath = router ? router.location.pathname : '';
    var activeTab = processedChildRoutes.find(function (child) {
      return currentPath.includes(child.path);
    });

    if (!activeTab && processedChildRoutes.length) {
      updateRoute(processedChildRoutes[0].routeKey);
    }

    if (activeTab && onHandleSelect) {
      onHandleSelect(activeTab.routeKey);
    }

    return activeTab || {};
  }, [processedChildRoutes, onHandleSelect, router, updateRoute]);
  var changeTabs = useCallback(function (routeKey) {
    return function () {
      if (onHandleSelect) {
        onHandleSelect(routeKey);
      }

      updateRoute(routeKey);
      var newTab = findChildRoute(routeKey, processedChildRoutes);
      setActiveTab(newTab);
    };
  }, [findChildRoute, onHandleSelect, processedChildRoutes, updateRoute]);
  var handleSelect = useCallback(function (routeKey) {
    var nextTab = findChildRoute(routeKey, processedChildRoutes);
    var currentTab = processedChildRoutes[activeTab.keys];

    if (confirmTabChange) {
      confirmTabChange(nextTab, currentTab, changeTabs(routeKey));
    } else {
      changeTabs(routeKey)();
    }
  }, [activeTab, changeTabs, confirmTabChange, findChildRoute, processedChildRoutes]);
  /***
   * Hooks
   */

  var dataAttributes = useMemo(function () {
    return pickDataAttributes(props);
  }, [props]);
  var filteredTabs = useMemo(function () {
    return (processedChildRoutes || []).filter(function (tab) {
      return hasAccess(_objectSpread2(_objectSpread2({}, props), tab));
    });
  }, [processedChildRoutes, props]);
  useEffect(function () {
    setActiveTab(getActiveTab());
  }, [getActiveTab]);

  if (!alwaysShowTabs && processedChildRoutes.length < 2) {
    return null;
  }

  return /*#__PURE__*/React.createElement("nav", _extends({
    className: "nav-tabs ".concat(classNames)
  }, dataAttributes), filteredTabs && activeTab && (renderAsLegacy || props.chakra && props.chakra === 'off' ? /*#__PURE__*/React.createElement(NavTabs$1, {
    activeTab: activeTab,
    tabs: filteredTabs,
    onSelect: handleSelect
  }) : /*#__PURE__*/React.createElement(NavTabs$2, {
    activeTab: activeTab,
    tabs: filteredTabs,
    onChange: handleSelect
  })));
};

NavTabs.displayName = "NavTabs";
export { NavTabs };